import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Checkbox } from '@cencosud-cencommerce/paris-uikit';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import { Terms } from '@/modules/perseo-billing/components';
import { useGetCartTotals } from '@/modules/perseo-billing/hooks';
import { PaymentProcessor } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import { setPaymentAvailable } from '@/modules/perseo-billing/store/slices/billing';
import { useMediaQuery } from '@/modules/perseo-billing/ui/react/src';

import { SpreedlyPaymentButton } from '../SpreedlyPaymentButton';

export const spreedlyProcessors = [
  PaymentProcessor.TRANSBANK_CENCOSUD_CARD,
  PaymentProcessor.TRANSBANK
];

const LABEL_CENCOSUD = 'Total con tarjeta Cencosud:';
const LABEL_OTHERS = 'Total con otros medios de pago:';

const FormFooter = (props: FormBodyProps) => {
  const { isCvvValid, isCardNumberValid, errors, isCencosud } = props;
  const billing = useAppSelector((state) => state.billing);
  const { isPaymentAvailable, selectedPaymentMethods, activeAccordion } =
    billing;
  const selectedPaymentMethod = selectedPaymentMethods?.payments;

  const isSpreedlySelectedCase = selectedPaymentMethod?.some(
    ({ paymentProcessor }) => spreedlyProcessors.includes(paymentProcessor)
  );

  const isCencopaySelected = selectedPaymentMethod?.some(
    (payment) => payment.paymentProcessor === PaymentProcessor.CENCOPAY
  );

  const isActiveSpreedlyAccordion = spreedlyProcessors?.includes(
    activeAccordion?.accordionPaymentMethodId as unknown as PaymentProcessor
  );

  const dispatch = useAppDispatch();
  const form = useFormContext<IFormFields>();
  const { setValue, watch, register } = form;
  const isMobile = useMediaQuery('lg');
  const saveCard = watch('saveCard');
  const termsAndConditions = watch('termsAndConditions');
  const totals = useGetCartTotals();
  const { otherMethods, cencosudCard } = totals;

  useEffect(() => {
    setValue('saveCard', true);
    setValue('termsAndConditions', true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const selectorSubmitButton = isMobile
      ? '[data-testid="purchase-summary-continue-button"]'
      : '#submit-button';
    const submitButton = document.querySelector(
      selectorSubmitButton
    ) as HTMLButtonElement;

    if (!submitButton) return;

    if (isCvvValid && isCardNumberValid && termsAndConditions) {
      dispatch(setPaymentAvailable(true));
      submitButton.disabled = false;
      return;
    }
    submitButton.disabled = !isPaymentAvailable || errors.length > 0;
    if (isSpreedlySelectedCase) {
      dispatch(setPaymentAvailable(false));
    }
    return () => {
      if (isSpreedlySelectedCase) {
        submitButton.disabled = true;
        dispatch(setPaymentAvailable(false));
      }
    };
  }, [
    dispatch,
    termsAndConditions,
    isCvvValid,
    isCardNumberValid,
    isPaymentAvailable,
    errors,
    isMobile,
    isSpreedlySelectedCase
  ]);
  useEffect(() => {
    if (isActiveSpreedlyAccordion && isCencopaySelected) {
      setTimeout(() => {
        dispatch(setPaymentAvailable(false));
      }, 380);
    }
  }, [
    isActiveSpreedlyAccordion,
    dispatch,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(selectedPaymentMethod),
    isCencopaySelected
  ]);

  const title = isCencosud ? LABEL_CENCOSUD : LABEL_OTHERS;

  const withGiftCardPrice = totals?.hasGiftCardApplied;
  const cencosudPrice = withGiftCardPrice
    ? withGiftCardPrice
    : cencosudCard?.price;

  const otherMethodsPrice = withGiftCardPrice
    ? withGiftCardPrice
    : otherMethods?.price;

  const price = isCencosud ? cencosudPrice : otherMethodsPrice;

  return (
    <div className="co-flex co-flex-col co-gap-16px tablet_w:co-mt-3">
      <div
        className={`co-contents co-flex-col co-gap-4 ${isCvvValid ? '' : 'co-hidden co-h-0'}`}
      >
        <Checkbox
          label="Quiero guardar esta tarjeta."
          {...register('saveCard', {
            onChange: (e) => {
              setValue('saveCard', e.target.checked);
            }
          })}
          defaultChecked={saveCard}
        />

        <Checkbox
          label={<Terms />}
          {...register('termsAndConditions', {
            onChange: (e) => {
              setValue('termsAndConditions', e.target.checked);
            }
          })}
          defaultChecked={termsAndConditions}
        />
      </div>

      {!isMobile && (
        <div className="co-flex co-gap-24px co-items-center">
          <div className="co-flex co-gap-16px co-items-center desktop:co-shrink-0">
            <p className="co-text-body1 co-text-[#0B0B0B] co-leading-[20px] co-text-ellipsis co-whitespace-nowrap">
              {title}
            </p>
            <p className="co-font-semibold co-text-[#1A1A1A]">{price}</p>
          </div>
          <SpreedlyPaymentButton
            isDisabled={!billing.isPaymentAvailable || errors.length > 0}
          />
        </div>
      )}
    </div>
  );
};

export default FormFooter;
